import api from './api';

export interface FlightSearchParams {
  iata_from: string;
  iata_to: string;
  date_outbound: string;
  date_inbound?: string;
  adults: number;
  children: number;
  babies: number;
  class_service: number;
  is_loyalty: number;
}

export interface MappedFlight {
  uniqueId: string;
  flightNumber: string;
  airlineName: string;
  airlineCode?: string;
  departureLocation: string;
  arrivalLocation: string;
  departureTime: string;
  arrivalTime: string;
  flightDuration: string;
  ourPrice: number;
  tax: number;
  qntStop: number;
  cabin?: string;
  stops: any[];
}

function mapStop(s: any, i: number) {
  return {
    stopOrder: s.STOP_ORDER ?? s.stopOrder ?? i + 1,
    op: s.OP ?? s.op ?? '',
    isEscale: s.IS_ESCALE ?? s.isEscale ?? false,
    flightNumber: s.FLIGHT_NUMBER ?? s.flightNumber ?? '',
    standardizedFlightNumber: s.STANDARDIZED_FLIGHT_NUMBER ?? s.standardizedFlightNumber,
    departureTime: s.DEPARTURE_TIME ?? s.departureTime ?? '',
    arrivalTime: s.ARRIVAL_TIME ?? s.arrivalTime ?? '',
    departureLocation: s.DEPARTURE_LOCATION ?? s.departureLocation ?? '',
    arrivalLocation: s.ARRIVAL_LOCATION ?? s.arrivalLocation ?? '',
    flightDuration: s.FLIGHT_DURATION ?? s.flightDuration ?? '',
    timeWaiting: s.TIME_WAITING ?? s.timeWaiting,
    companyCode: s.COMPANY_CODE ?? s.companyCode,
    operantCompany: s.OPERANT_COMPANY ?? s.operantCompany,
    cabin: s.CABIN ?? s.cabin,
    qntEscale: s.QNT_ESCALE ?? s.qntEscale ?? 0,
  };
}

function mapFlight(key: string, f: any): MappedFlight {
  return {
    uniqueId: f.UNIQUE_ID || key,
    flightNumber: f.STANDARDIZED_FLIGHT_NUMBER || f.FLIGHT_NUMBER || key,
    airlineName: f.AIRLINE?.NAME || '',
    airlineCode: f.AIRLINE?.CODE_CIA || f.AIRLINE?.CODE,
    departureLocation: f.DEPARTURE_LOCATION || '',
    arrivalLocation: f.ARRIVAL_LOCATION || '',
    departureTime: f.DEPARTURE_TIME || '',
    arrivalTime: f.ARRIVAL_TIME || '',
    flightDuration: f.TOTAL_FLIGHT_DURATION || f.FLIGHT_DURATION || '',
    ourPrice: Number(f.OUR_PRICE || 0),
    tax: Number(f.TAX || f.TOTAL_TAX || 0),
    qntStop: Number(f.QNT_STOP || 0),
    cabin: f.CABIN,
    stops: (f.STOPS || f.stops || []).map(mapStop),
  };
}

export const flightService = {
  async searchFlights(params: FlightSearchParams): Promise<{
    outboundFlights: MappedFlight[];
    returnFlights?: MappedFlight[];
    route: string;
  }> {
    const response = await api.post('/write/flights/search', params);
    const inner = response.data?.data?.data ?? response.data?.data ?? {};

    const route = `${params.iata_from}-${params.iata_to}`;

    // Round-trip: GROUPS com OUTBOUND/INBOUND keys
    if (inner.GROUPS && Array.isArray(inner.GROUPS) && inner.FLIGHTS) {
      const sortedIds: string[] = inner.SORTED_FLIGHT_IDS || [];

      // separar outbound/inbound a partir dos grupos
      const outboundSet = new Set<string>();
      const inboundSet = new Set<string>();
      inner.GROUPS.forEach((g: any) => {
        (g.OUTBOUND || []).forEach((k: string) => outboundSet.add(k));
        (g.INBOUND || []).forEach((k: string) => inboundSet.add(k));
      });

      // usar SORTED_FLIGHT_IDS para preservar ordem do backend; fallback para Set
      const ordered = sortedIds.length ? sortedIds : [...Array.from(outboundSet), ...Array.from(inboundSet)];

      const outboundFlights = ordered
        .filter(k => outboundSet.has(k) && inner.FLIGHTS[k])
        .map(k => mapFlight(k, inner.FLIGHTS[k]));

      const returnFlights = ordered
        .filter(k => inboundSet.has(k) && inner.FLIGHTS[k])
        .map(k => mapFlight(k, inner.FLIGHTS[k]));

      return { outboundFlights, returnFlights, route };
    }

    // One-way: FLIGHTS object, optionally ordered by SORTED_FLIGHT_IDS
    if (inner.FLIGHTS) {
      const ids: string[] = inner.SORTED_FLIGHT_IDS || Object.keys(inner.FLIGHTS);
      const outboundFlights = ids
        .filter(k => inner.FLIGHTS[k])
        .map(k => mapFlight(k, inner.FLIGHTS[k]));
      return { outboundFlights, route };
    }

    return { outboundFlights: [], route };
  },

  async createPaymentLink(dto: any): Promise<{ checkoutId: string; paymentUrl: string; sandboxUrl: string }> {
    const response = await api.post('/payments/payment-link', dto);
    return response.data;
  },
};
